import { api, handleApiError } from '../client'
import { buildODataQuery, type ODataQueryParams } from '../odata'

export interface WorkflowOData {
  id: string
  name: string
  description?: string
  status: 'active' | 'inactive' | 'draft'
  steps: number
  lastRunAt?: string
  totalRuns: number
  successRate: number
  createdAt: string
  updatedAt: string
}

export interface WorkflowsODataResponse {
  value: WorkflowOData[]
  '@odata.count'?: number
}

export interface WorkflowStep {
  id: string
  name: string
  type: 'trigger' | 'action' | 'condition' | 'delay'
  config: Record<string, unknown>
  position: { x: number; y: number }
  nextSteps?: string[]
}

export interface WorkflowDetail extends Omit<WorkflowOData, 'steps'> {
  steps: WorkflowStep[] | number
  stepCount?: number
  definition?: string
  triggers?: WorkflowTrigger[]
  variables?: WorkflowVariable[]
}

export interface WorkflowTrigger {
  id: string
  type: 'manual' | 'schedule' | 'webhook' | 'event'
  config: Record<string, unknown>
}

export interface WorkflowVariable {
  name: string
  type: 'string' | 'number' | 'boolean' | 'date' | 'array'
  defaultValue?: unknown
}

export interface WorkflowRun {
  id: string
  workflowId: string
  status: 'running' | 'completed' | 'failed' | 'cancelled'
  startedAt: string
  completedAt?: string
  duration?: number
  error?: string
  stepsCompleted: number
  totalSteps: number
}

export interface WorkflowRunsODataResponse {
  value: WorkflowRun[]
  '@odata.count'?: number
}

export interface CreateWorkflowRequest {
  name: string
  description?: string
  definition?: string
  steps?: WorkflowStep[]
  triggers?: WorkflowTrigger[]
  variables?: WorkflowVariable[]
}

export interface UpdateWorkflowRequest {
  name?: string
  description?: string
  definition?: string
  steps?: number
  saveVersion?: boolean
  changeDescription?: string
}

export interface WorkflowStats {
  totalWorkflows: number
  activeWorkflows: number
  totalRuns: number
  avgSuccessRate: number
  avgStepsPerWorkflow: number
}

export interface WorkflowVersion {
  id: string
  workflowId: string
  versionNumber: number
  definition?: string
  changeDescription?: string
  createdByUserId: string
  createdAt: string
}

export interface WorkflowVersionsResponse {
  value: WorkflowVersion[]
}

export const workflowService = {
  /**
   * Get workflows with OData support
   */
  async getWorkflows(params?: ODataQueryParams): Promise<WorkflowsODataResponse> {
    try {
      const queryString = params ? buildODataQuery(params) : ''
      const response = await api.get<WorkflowsODataResponse | WorkflowOData[]>(`/v1/workflows${queryString}`)

      if (Array.isArray(response.data)) {
        return {
          value: response.data,
          '@odata.count': response.data.length
        }
      }
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Get workflow by ID
   */
  async getWorkflow(id: string): Promise<WorkflowDetail> {
    try {
      const response = await api.get<WorkflowDetail>(`/v1/workflows/${id}`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Create a new workflow
   */
  async createWorkflow(data: CreateWorkflowRequest): Promise<WorkflowDetail> {
    try {
      const response = await api.post<WorkflowDetail>('/v1/workflows', data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Update a workflow
   */
  async updateWorkflow(id: string, data: UpdateWorkflowRequest): Promise<WorkflowDetail> {
    try {
      const response = await api.put<WorkflowDetail>(`/v1/workflows/${id}`, data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Delete a workflow
   */
  async deleteWorkflow(id: string): Promise<void> {
    try {
      await api.delete(`/v1/workflows/${id}`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Activate a workflow
   */
  async activateWorkflow(id: string): Promise<WorkflowOData> {
    try {
      const response = await api.post<WorkflowOData>(`/v1/workflows/${id}/activate`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Deactivate a workflow
   */
  async deactivateWorkflow(id: string): Promise<WorkflowOData> {
    try {
      const response = await api.post<WorkflowOData>(`/v1/workflows/${id}/deactivate`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Run a workflow manually
   */
  async runWorkflow(id: string, variables?: Record<string, unknown>): Promise<WorkflowRun> {
    try {
      const response = await api.post<WorkflowRun>(`/v1/workflows/${id}/run`, { variables })
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Get workflow runs/history
   */
  async getWorkflowRuns(workflowId: string, params?: ODataQueryParams): Promise<WorkflowRunsODataResponse> {
    try {
      const queryString = params ? buildODataQuery(params) : ''
      const response = await api.get<WorkflowRunsODataResponse | WorkflowRun[]>(
        `/v1/workflows/${workflowId}/runs${queryString}`
      )

      if (Array.isArray(response.data)) {
        return {
          value: response.data,
          '@odata.count': response.data.length
        }
      }
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Get a specific workflow run
   */
  async getWorkflowRun(workflowId: string, runId: string): Promise<WorkflowRun> {
    try {
      const response = await api.get<WorkflowRun>(`/v1/workflows/${workflowId}/runs/${runId}`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Cancel a running workflow
   */
  async cancelWorkflowRun(workflowId: string, runId: string): Promise<void> {
    try {
      await api.post(`/v1/workflows/${workflowId}/runs/${runId}/cancel`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Duplicate a workflow
   */
  async duplicateWorkflow(id: string, name: string): Promise<WorkflowDetail> {
    try {
      const response = await api.post<WorkflowDetail>(`/v1/workflows/${id}/duplicate`, { name })
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Get workflow statistics
   */
  async getStats(): Promise<WorkflowStats> {
    try {
      const response = await api.get<WorkflowStats>('/v1/workflows/stats')
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Export workflow as JSON
   */
  async exportWorkflow(id: string): Promise<Blob> {
    try {
      const response = await api.get<Blob>(`/v1/workflows/${id}/export`, {
        responseType: 'blob',
      })
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Import workflow from JSON
   */
  async importWorkflow(file: File): Promise<WorkflowDetail> {
    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await api.post<WorkflowDetail>('/v1/workflows/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Get all versions of a workflow
   */
  async getWorkflowVersions(workflowId: string): Promise<WorkflowVersion[]> {
    try {
      const response = await api.get<WorkflowVersion[]>(`/v1/workflows/${workflowId}/versions`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Get a specific version of a workflow
   */
  async getWorkflowVersion(workflowId: string, versionNumber: number): Promise<WorkflowVersion> {
    try {
      const response = await api.get<WorkflowVersion>(
        `/v1/workflows/${workflowId}/versions/${versionNumber}`
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Save a new version of a workflow
   */
  async saveWorkflowVersion(workflowId: string, changeDescription?: string): Promise<WorkflowVersion> {
    try {
      const params = changeDescription ? `?changeDescription=${encodeURIComponent(changeDescription)}` : ''
      const response = await api.post<WorkflowVersion>(
        `/v1/workflows/${workflowId}/versions${params}`
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Restore a workflow to a specific version
   */
  async restoreWorkflowVersion(workflowId: string, versionNumber: number): Promise<WorkflowDetail> {
    try {
      const response = await api.post<WorkflowDetail>(
        `/v1/workflows/${workflowId}/restore/${versionNumber}`
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },
}
